import React, { useState } from 'react';
import { XIcon, ChevronDownIcon, ChevronRightIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface MobileFilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedFilters: {
    brands: string[];
    priceRange: {
      min: number;
      max: number;
    };
    categories: string[];
    needsPrescription: boolean;
    isExpress: boolean;
    isGlobal: boolean;
    offers: string[];
  };
  onFilterChange: (filters: any) => void;
  hideCategories?: boolean;
}
export function MobileFilterModal({
  isOpen,
  onClose,
  selectedFilters,
  onFilterChange,
  hideCategories = false
}: MobileFilterModalProps) {
  const [expandedSections, setExpandedSections] = useState({
    price: true,
    brands: true,
    categories: true,
    offers: true
  });
  const brands = ['Pampers', 'Huggies', 'Babyjoy', 'Fine Baby', 'Bambi', 'Molfix'];
  const categories = ['Baby Diapers', 'Training Pants', 'Swim Diapers', 'Overnight Diapers', 'Eco-Friendly Diapers'];
  const offers = ['On Sale', 'Special Offers', 'Buy One Get One', 'Clearance'];
  const toggleSection = (section: string) => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section]
    });
  };
  const handleBrandToggle = (brand: string) => {
    const newBrands = selectedFilters.brands.includes(brand) ? selectedFilters.brands.filter(b => b !== brand) : [...selectedFilters.brands, brand];
    onFilterChange({
      ...selectedFilters,
      brands: newBrands
    });
  };
  const handleCategoryToggle = (category: string) => {
    const newCategories = selectedFilters.categories.includes(category) ? selectedFilters.categories.filter(c => c !== category) : [...selectedFilters.categories, category];
    onFilterChange({
      ...selectedFilters,
      categories: newCategories
    });
  };
  const handleOfferToggle = (offer: string) => {
    const newOffers = selectedFilters.offers.includes(offer) ? selectedFilters.offers.filter(o => o !== offer) : [...selectedFilters.offers, offer];
    onFilterChange({
      ...selectedFilters,
      offers: newOffers
    });
  };
  const handleReset = () => {
    onFilterChange({
      brands: [],
      priceRange: {
        min: 0,
        max: 5000
      },
      categories: [],
      needsPrescription: false,
      offers: []
    });
  };
  return <AnimatePresence data-id="element-1420" data-mp="10vy9nk9m00hm">
      {isOpen && <>
          {/* Overlay */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={onClose} data-id="element-1421" data-mp="vxwo8sopnp2z" />
          {/* Bottom Sheet */}
          <motion.div initial={{
        y: '100%'
      }} animate={{
        y: 0
      }} exit={{
        y: '100%'
      }} transition={{
        type: 'spring',
        damping: 30,
        stiffness: 300
      }} className="fixed bottom-0 left-0 right-0 bg-white z-50 shadow-2xl rounded-t-2xl flex flex-col max-h-[90vh]" data-id="element-1422" data-mp="2h9oggys452uy">
            {/* Drag Handle */}
            <div className="flex justify-center pt-2 pb-1 flex-shrink-0" data-id="element-1423" data-mp="37kqg4eob08o1">
              <div className="w-10 h-1 bg-gray-300 rounded-full" data-id="element-1424" data-mp="3chqbnvnj1t0w" />
            </div>
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between flex-shrink-0" data-id="element-1425" data-mp="2xtm2biad6tus">
              <h2 className="text-lg font-bold text-gray-900" data-id="element-1426" data-mp="32r5feevbt0l5">Filters</h2>
              <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100" aria-label="Close filters" data-id="element-1427" data-mp="3qh9jsogc94d7">
                <XIcon className="w-5 h-5 text-gray-500" data-id="element-1428" data-mp="27m95pv61qaci" />
              </button>
            </div>
            {/* Filter Content - Scrollable */}
            <div className="flex-1 overflow-y-auto overscroll-contain" data-id="element-1429" data-mp="22o7kp4p280ye">
              {/* Price Filter */}
              <div className="border-b border-gray-100" data-id="element-1430" data-mp="1xov06qlnv5yz">
                <button onClick={() => toggleSection('price')} className="flex justify-between items-center w-full p-4 text-left" data-id="element-1431" data-mp="1sp8mkg1x7npn">
                  <h3 className="text-base font-bold text-gray-900" data-id="element-1432" data-mp="2ldnoq1zyebjk">
                    Filter By Price
                  </h3>
                  {expandedSections.price ? <ChevronDownIcon className="w-5 h-5 text-gray-500" data-id="element-1433" data-mp="2bn2wqxesc59m" /> : <ChevronRightIcon className="w-5 h-5 text-gray-500" data-id="element-1434" data-mp="391x8yzhiqr8l" />}
                </button>
                {expandedSections.price && <div className="px-4 pb-4" data-id="element-1435" data-mp="349ds9tnjbfzk">
                    <div className="flex justify-between mb-2 text-sm text-gray-700" data-id="element-1436" data-mp="3ckwb7cqhxth7">
                      <span data-id="element-1437" data-mp="3glgaomyjl96i">{selectedFilters.priceRange.min} SAR</span>
                      <span data-id="element-1438" data-mp="3gnj7pqy442kq">— {selectedFilters.priceRange.max} SAR</span>
                    </div>
                    <div className="relative h-2 bg-gray-200 rounded-full" data-id="element-1439" data-mp="3lmvqo0lv4pi1">
                      <div className="absolute h-full w-1/3 bg-[#0066CC] rounded-full" data-id="element-1440" data-mp="3qlgtm7si1na6"></div>
                    </div>
                  </div>}
              </div>
              {/* Brand Filter */}
              <div className="border-b border-gray-100" data-id="element-1441" data-mp="3d3dh2a88mgf">
                <button onClick={() => toggleSection('brands')} className="flex justify-between items-center w-full p-4 text-left" data-id="element-1442" data-mp="85mte5wdztoe">
                  <h3 className="text-base font-bold text-gray-900" data-id="element-1443" data-mp="i4lzphtojd88">Brands</h3>
                  {expandedSections.brands ? <ChevronDownIcon className="w-5 h-5 text-gray-500" data-id="element-1444" data-mp="n3ypobful0f1" /> : <ChevronRightIcon className="w-5 h-5 text-gray-500" data-id="element-1445" data-mp="3tl78u9gddoqj" />}
                </button>
                {expandedSections.brands && <div className="px-4 pb-4" data-id="element-1446" data-mp="74kylc3k9m7f">
                    <input type="text" placeholder="Search brands" className="w-full p-2 mb-3 border border-gray-300 rounded-md text-sm" data-id="element-1447" data-mp="76e2ijmshsbq" />
                    <div className="space-y-3 max-h-48 overflow-y-auto" data-id="element-1448" data-mp="g7c9nasiyfyj">
                      {brands.map(brand => <label key={brand} className="flex items-center cursor-pointer" data-id="element-1449" data-mp="q3fa8c7qa6jd">
                          <input type="checkbox" checked={selectedFilters.brands.includes(brand)} onChange={() => handleBrandToggle(brand)} className="w-5 h-5 text-[#0066CC] border-gray-300 rounded" data-id="element-1450" data-mp="l2j6pzragmeh" />
                          <span className="ml-3 text-sm text-gray-700" data-id="element-1451" data-mp="17dj5h8pkj2tc">
                            {brand}
                          </span>
                        </label>)}
                    </div>
                  </div>}
              </div>
              {/* Category Filter */}
              {!hideCategories && <div className="border-b border-gray-100" data-id="element-1452" data-mp="jpjhggjn5qig">
                <button onClick={() => toggleSection('categories')} className="flex justify-between items-center w-full p-4 text-left" data-id="element-1453" data-mp="nq3e7j0ays42">
                  <h3 className="text-base font-bold text-gray-900" data-id="element-1454" data-mp="enke36nwitli">
                    Categories
                  </h3>
                  {expandedSections.categories ? <ChevronDownIcon className="w-5 h-5 text-gray-500" data-id="element-1455" data-mp="292dah7wlzoei" /> : <ChevronRightIcon className="w-5 h-5 text-gray-500" data-id="element-1456" data-mp="vvil2on5xqe0" />}
                </button>
                {expandedSections.categories && <div className="px-4 pb-4" data-id="element-1457" data-mp="ruqb1t6gvhgo">
                    <div className="space-y-3" data-id="element-1458" data-mp="n2ordm6yyogm">
                      {categories.map(category => <label key={category} className="flex items-center cursor-pointer" data-id="element-1459" data-mp="i4x26zxplgg4">
                          <input type="checkbox" checked={selectedFilters.categories.includes(category)} onChange={() => handleCategoryToggle(category)} className="w-5 h-5 text-[#0066CC] border-gray-300 rounded" data-id="element-1460" data-mp="85xqf8hnd4ow" />
                          <span className="ml-3 text-sm text-gray-700" data-id="element-1461" data-mp="1tjk3yu08gclh">
                            {category}
                          </span>
                        </label>)}
                    </div>
                  </div>}
                </div>}
              {/* Prescription Toggle */}
              <div className="border-b border-gray-100 p-4" data-id="element-1462" data-mp="2at4s74tyo4w7">
                <label className="flex items-center justify-between cursor-pointer" data-id="element-1463" data-mp="2fs7kdoouegmr">
                  <span className="text-base font-bold text-gray-900" data-id="element-1464" data-mp="1i4794zhxe42e">
                    Needs Prescription
                  </span>
                  <div className="relative" data-id="element-1465" data-mp="1n43d4yqmhvhy">
                    <input type="checkbox" checked={selectedFilters.needsPrescription} onChange={e => onFilterChange({
                  ...selectedFilters,
                  needsPrescription: e.target.checked
                })} className="sr-only peer" data-id="element-1466" data-mp="1n4v9ibs1nx9a" />
                    <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#0066CC]" data-id="element-1467" data-mp="11key19qqjkt4"></div>
                  </div>
                </label>
              </div>
              {/* Offers Filter */}
              <div className="border-b border-gray-100" data-id="element-1468" data-mp="ilkmsaqhd2hp">
                <button onClick={() => toggleSection('offers')} className="flex justify-between items-center w-full p-4 text-left" data-id="element-1469" data-mp="in5gjy4z0hei">
                  <h3 className="text-base font-bold text-gray-900" data-id="element-1470" data-mp="sl1nseuses7c">Offers</h3>
                  {expandedSections.offers ? <ChevronDownIcon className="w-5 h-5 text-gray-500" data-id="element-1471" data-mp="23ydwb31hryzu" /> : <ChevronRightIcon className="w-5 h-5 text-gray-500" data-id="element-1472" data-mp="28x7209ajelpg" />}
                </button>
                {expandedSections.offers && <div className="px-4 pb-4" data-id="element-1473" data-mp="3teg4nhw8yx21">
                    <div className="space-y-3" data-id="element-1474" data-mp="3ofmrxrosba2x">
                      {offers.map(offer => <label key={offer} className="flex items-center cursor-pointer" data-id="element-1475" data-mp="3jowleb4mh16n">
                          <input type="checkbox" checked={selectedFilters.offers.includes(offer)} onChange={() => handleOfferToggle(offer)} className="w-5 h-5 text-[#0066CC] border-gray-300 rounded" data-id="element-1476" data-mp="3eoionp8h1dnx" />
                          <span className="ml-3 text-sm text-gray-700" data-id="element-1477" data-mp="2vsafwbg92knv">
                            {offer}
                          </span>
                        </label>)}
                    </div>
                  </div>}
              </div>
            </div>
            {/* Footer Actions - Sticky at bottom of sheet */}
            <div className="bg-white border-t border-gray-200 p-4 flex gap-3 flex-shrink-0 safe-area-bottom" data-id="element-1478" data-mp="2oehwy4y02e8c">
              <button onClick={handleReset} className="flex-1 py-3 px-4 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-1479" data-mp="2ei6k9duhzmdd">
                Clear All
              </button>
              <button onClick={onClose} className="flex-1 py-3 px-4 bg-[#0066CC] text-white rounded-lg text-sm font-medium hover:bg-[#0052a3]" data-id="element-1480" data-mp="37859pacltlik">
                Apply Filters
              </button>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}